import {setOutlookEmailSyncHeader} from './outlookComposeItem';
import type {RoamingSettingsState} from './RoamingSettingsContext';
import {
  type ComposeMessageMetadata,
  type DialogMessagePayload,
  getParentDialogToken,
  parseEmailSyncHeaderSetMessage,
  parseParentAttachmentsMessage,
  parseParentComposeMessageMetadataMessage,
  parseParentRoamingSettingsMessage,
  parseRoamingSettingsUpdatedMessage,
  type RoamingSettingsUpdate,
  stringifyDialogMessage,
} from './sendDialogProtocol';
import type {MessageAttachment} from './SendModal/EmailSync/structs';

export type {ComposeMessageMetadata, RoamingSettingsUpdate};

type AttachmentListener = (attachments: MessageAttachment[]) => void;
type RoamingSettingsListener = (settings: RoamingSettingsState) => void;
type ComposeMessageMetadataListener = (
  metadata: ComposeMessageMetadata
) => void;

const attachmentListeners = new Set<AttachmentListener>();
const roamingSettingsListeners = new Set<RoamingSettingsListener>();
const composeMessageMetadataListeners =
  new Set<ComposeMessageMetadataListener>();
let started = false;
let listenerReadyPromise: Promise<void> | null = null;
let resolveListenerReady: (() => void) | null = null;

let pendingEmailSyncHeader:
  | {
      resolve: () => void;
      reject: (error: Error) => void;
      timeoutId: number;
    }
  | undefined;

let pendingRoamingSettingsRequest:
  | {
      resolve: (settings: RoamingSettingsState) => void;
      reject: (error: Error) => void;
      timeoutId: number;
    }
  | undefined;

let pendingRoamingSettingsUpdate:
  | {
      resolve: (settings: RoamingSettingsState) => void;
      reject: (error: Error) => void;
      timeoutId: number;
    }
  | undefined;

let pendingComposeMessageMetadataRequest:
  | {
      resolve: (metadata: ComposeMessageMetadata) => void;
      reject: (error: Error) => void;
      timeoutId: number;
    }
  | undefined;

let inFlightComposeMessageMetadataRequest: Promise<ComposeMessageMetadata> | null =
  null;

let inFlightRoamingSettingsRequest: Promise<RoamingSettingsState> | null = null;

const notifyAttachmentListeners = (attachments: MessageAttachment[]) => {
  for (const listener of attachmentListeners) {
    listener(attachments);
  }
};

const notifyRoamingSettingsListeners = (settings: RoamingSettingsState) => {
  for (const listener of roamingSettingsListeners) {
    listener(settings);
  }
};

const notifyComposeMessageMetadataListeners = (
  metadata: ComposeMessageMetadata
) => {
  for (const listener of composeMessageMetadataListeners) {
    listener(metadata);
  }
};

const parentHandler = (arg: {message: string}) => {
  const headerSetMessage = parseEmailSyncHeaderSetMessage(arg.message);
  if (headerSetMessage && pendingEmailSyncHeader) {
    window.clearTimeout(pendingEmailSyncHeader.timeoutId);
    const pending = pendingEmailSyncHeader;
    pendingEmailSyncHeader = undefined;
    if (headerSetMessage.ok) {
      pending.resolve();
    } else {
      pending.reject(
        new Error(headerSetMessage.error ?? 'Failed to set email sync header')
      );
    }
    return;
  }

  const composeMessageMetadata = parseParentComposeMessageMetadataMessage(
    arg.message
  );
  if (composeMessageMetadata) {
    notifyComposeMessageMetadataListeners(composeMessageMetadata);
    if (pendingComposeMessageMetadataRequest) {
      window.clearTimeout(pendingComposeMessageMetadataRequest.timeoutId);
      const pending = pendingComposeMessageMetadataRequest;
      pendingComposeMessageMetadataRequest = undefined;
      inFlightComposeMessageMetadataRequest = null;
      pending.resolve(composeMessageMetadata);
    }
    return;
  }

  const roamingSettings = parseParentRoamingSettingsMessage(arg.message);
  if (roamingSettings) {
    notifyRoamingSettingsListeners(roamingSettings);
    if (pendingRoamingSettingsRequest) {
      window.clearTimeout(pendingRoamingSettingsRequest.timeoutId);
      const pending = pendingRoamingSettingsRequest;
      pendingRoamingSettingsRequest = undefined;
      inFlightRoamingSettingsRequest = null;
      pending.resolve(roamingSettings);
    }
    return;
  }

  const roamingSettingsUpdated = parseRoamingSettingsUpdatedMessage(
    arg.message
  );
  if (roamingSettingsUpdated && pendingRoamingSettingsUpdate) {
    window.clearTimeout(pendingRoamingSettingsUpdate.timeoutId);
    const pending = pendingRoamingSettingsUpdate;
    pendingRoamingSettingsUpdate = undefined;
    if (roamingSettingsUpdated.ok) {
      if (!roamingSettingsUpdated.settings) {
        pending.reject(
          new Error('Failed to update roaming settings: missing settings')
        );
        return;
      }
      notifyRoamingSettingsListeners(roamingSettingsUpdated.settings);
      pending.resolve(roamingSettingsUpdated.settings);
    } else {
      pending.reject(
        new Error(
          roamingSettingsUpdated.error ?? 'Failed to update roaming settings'
        )
      );
    }
    return;
  }

  const attachments = parseParentAttachmentsMessage(arg.message);
  if (attachments) {
    notifyAttachmentListeners(attachments);
  }
};

export const subscribeToSendDialogAttachments = (
  listener: AttachmentListener
): (() => void) => {
  attachmentListeners.add(listener);
  return () => {
    attachmentListeners.delete(listener);
  };
};

export const subscribeToSendDialogRoamingSettings = (
  listener: RoamingSettingsListener
): (() => void) => {
  roamingSettingsListeners.add(listener);
  return () => {
    roamingSettingsListeners.delete(listener);
  };
};

export const subscribeToComposeMessageMetadata = (
  listener: ComposeMessageMetadataListener
): (() => void) => {
  composeMessageMetadataListeners.add(listener);
  return () => {
    composeMessageMetadataListeners.delete(listener);
  };
};

export const whenSendDialogParentListenerReady = (): Promise<void> => {
  if (listenerReadyPromise) {
    return listenerReadyPromise;
  }

  listenerReadyPromise = new Promise((resolve) => {
    resolveListenerReady = resolve;
  });
  return listenerReadyPromise;
};

export const startSendDialogParentMessageListener = (): void => {
  if (started || typeof Office === 'undefined') {
    return;
  }
  started = true;
  whenSendDialogParentListenerReady();

  Office.context.ui.addHandlerAsync(
    Office.EventType.DialogParentMessageReceived,
    parentHandler,
    () => {
      messageSendDialogParent({type: 'ready'});
      resolveListenerReady?.();
      resolveListenerReady = null;
    }
  );
};

const assertSendDialogContext = (): void => {
  if (typeof Office === 'undefined' || !Office.context?.ui?.messageParent) {
    throw new Error('Send dialog parent messaging is not available');
  }
};

export const messageSendDialogParent = (
  payload: DialogMessagePayload
): void => {
  assertSendDialogContext();
  Office.context.ui.messageParent(
    stringifyDialogMessage({
      ...payload,
      parentDialogToken: getParentDialogToken(),
    })
  );
};

export const requestComposeMessageMetadataFromParent =
  (): Promise<ComposeMessageMetadata> => {
    assertSendDialogContext();
    if (inFlightComposeMessageMetadataRequest) {
      return inFlightComposeMessageMetadataRequest;
    }

    inFlightComposeMessageMetadataRequest = new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        const pending = pendingComposeMessageMetadataRequest;
        if (!pending) {
          return;
        }
        pendingComposeMessageMetadataRequest = undefined;
        inFlightComposeMessageMetadataRequest = null;
        pending.reject(
          new Error('Timed out fetching compose message metadata')
        );
      }, 10_000);

      pendingComposeMessageMetadataRequest = {
        resolve: (metadata) => {
          window.clearTimeout(timeoutId);
          pendingComposeMessageMetadataRequest = undefined;
          inFlightComposeMessageMetadataRequest = null;
          resolve(metadata);
        },
        reject: (error: Error) => {
          window.clearTimeout(timeoutId);
          pendingComposeMessageMetadataRequest = undefined;
          inFlightComposeMessageMetadataRequest = null;
          reject(error);
        },
        timeoutId,
      };

      void whenSendDialogParentListenerReady().then(() => {
        messageSendDialogParent({type: 'getComposeMessageMetadata'});
      });
    });

    return inFlightComposeMessageMetadataRequest;
  };

export const requestRoamingSettingsFromParent =
  (): Promise<RoamingSettingsState> => {
    assertSendDialogContext();
    if (inFlightRoamingSettingsRequest) {
      return inFlightRoamingSettingsRequest;
    }

    inFlightRoamingSettingsRequest = new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        const pending = pendingRoamingSettingsRequest;
        if (!pending) {
          return;
        }
        pendingRoamingSettingsRequest = undefined;
        inFlightRoamingSettingsRequest = null;
        pending.reject(new Error('Timed out fetching roaming settings'));
      }, 10_000);

      pendingRoamingSettingsRequest = {
        resolve: (settings) => {
          window.clearTimeout(timeoutId);
          pendingRoamingSettingsRequest = undefined;
          inFlightRoamingSettingsRequest = null;
          resolve(settings);
        },
        reject: (error: Error) => {
          window.clearTimeout(timeoutId);
          pendingRoamingSettingsRequest = undefined;
          inFlightRoamingSettingsRequest = null;
          reject(error);
        },
        timeoutId,
      };

      void whenSendDialogParentListenerReady().then(() => {
        messageSendDialogParent({type: 'getRoamingSettings'});
      });
    });

    return inFlightRoamingSettingsRequest;
  };

export const requestUpdateRoamingSettingsOnParent = (
  updates: RoamingSettingsUpdate
): Promise<RoamingSettingsState> => {
  assertSendDialogContext();
  if (pendingRoamingSettingsUpdate) {
    return Promise.reject(
      new Error('Another roaming settings update is in progress')
    );
  }

  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      const pending = pendingRoamingSettingsUpdate;
      if (!pending) {
        return;
      }
      pendingRoamingSettingsUpdate = undefined;
      pending.reject(new Error('Timed out updating roaming settings'));
    }, 10_000);

    pendingRoamingSettingsUpdate = {
      resolve: (settings) => {
        window.clearTimeout(timeoutId);
        resolve(settings);
      },
      reject: (error: Error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      },
      timeoutId,
    };

    messageSendDialogParent({type: 'updateRoamingSettings', updates});
  });
};

export const requestSetOutlookEmailSyncHeader = (
  headerId: string
): Promise<void> => {
  if (typeof Office === 'undefined' || !Office.context?.ui?.messageParent) {
    return setOutlookEmailSyncHeader(headerId);
  }
  if (pendingEmailSyncHeader) {
    return Promise.reject(
      new Error('Another email sync header is in progress')
    );
  }

  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      const pending = pendingEmailSyncHeader;
      if (!pending) {
        return;
      }
      pendingEmailSyncHeader = undefined;
      pending.reject(new Error('Timed out setting email sync header'));
    }, 10_000);

    pendingEmailSyncHeader = {
      resolve: () => {
        window.clearTimeout(timeoutId);
        resolve();
      },
      reject: (error: Error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      },
      timeoutId,
    };

    messageSendDialogParent({type: 'setEmailSyncHeader', headerId});
  });
};
